import { createSlice } from '@reduxjs/toolkit';
// utils
import { callApi } from '../../utils/apiCall';

const initialState = {
    currentRound: null,
    loading: false,
    error: null,
};

const contactRoundSlice = createSlice({
    name: 'contactRound',
    initialState,
    reducers: {
        setCurrentRound(state, action) {
            state.currentRound = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        clearRound(state) {
            state.currentRound = null;
            state.loading = false;
            state.error = null;
        },
    },
});

export const { setCurrentRound, setLoading, setError, clearRound } = contactRoundSlice.actions;

// Selectors
export const selectCurrentRound = (state) => state.contactRound.currentRound;
export const selectRoundLoading = (state) => state.contactRound.loading;

// Thunks

/**
 * Open or reuse a ContactRound for a lead treatment.
 * @param {number} practiceId
 * @param {number} leadId
 * @param {number} treatmentId
 * @param {object} channels - optional { phone: bool, whatsapp: bool, email: bool }
 */
export function openContactRound(practiceId, leadId, treatmentId, channels = null) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const body = { lead_treatment: treatmentId };
            if (channels) body.channels = channels;

            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/contact-rounds/`,
                'post',
                body
            );
            dispatch(setCurrentRound(response.data));
            return response.data;
        } catch (err) {
            dispatch(setError(err.message));
            return null;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

/**
 * Update channels on an open round.
 */
export function updateRoundChannels(practiceId, leadId, roundId, channels) {
    return async (dispatch) => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/contact-rounds/${roundId}/`,
                'patch',
                { channels_used: channels }
            );
            dispatch(setCurrentRound(response.data));
            return response.data;
        } catch (err) {
            dispatch(setError(err.message));
            return null;
        }
    };
}

/**
 * Complete a ContactRound with an outcome.
 * Returns the completed round with updated contacted_count.
 */
export function completeContactRound(practiceId, leadId, roundId, { outcome, notes = '' }) {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/contact-rounds/${roundId}/complete/`,
                'post',
                { outcome, notes }
            );
            dispatch(setCurrentRound(response.data));
            return response.data;
        } catch (err) {
            dispatch(setError(err.message));
            return null;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

/**
 * Create a ContactRound and immediately complete it.
 * Used when the user submits a note — we don't need a "live" round,
 * just a completed one for count tracking.
 */
export function createAndCompleteRound(practiceId, leadId, treatmentId, { outcome, notes = '', channels = null }) {
    return async (dispatch) => {
        try {
            const defaultChannels = channels || { phone: true, whatsapp: false, email: false };
            const body = { lead_treatment: treatmentId, channels: defaultChannels };

            const openResponse = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/contact-rounds/`,
                'post',
                body
            );
            const roundId = openResponse?.data?.id;
            if (!roundId) return null;

            const completeResponse = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/contact-rounds/${roundId}/complete/`,
                'post',
                { outcome, notes }
            );
            dispatch(setCurrentRound(completeResponse?.data || null));
            return completeResponse?.data;
        } catch (err) {
            dispatch(setError(err.message));
            return null;
        }
    };
}

export default contactRoundSlice.reducer;
